<template>
  <div ref="playerRef">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef, ref } from "vue";
import Plyr, { Options } from "plyr";
import "plyr/dist/plyr.css";

interface Props {
  options?: Options;
}

const props = defineProps<Props>();
const playerRef = useTemplateRef<HTMLElement>("playerRef");

const player = ref<Plyr>();
let mediaElement: HTMLMediaElement | null = null;
let detachDurationListeners: (() => void) | null = null;

onMounted(() => {
  const root = playerRef.value;

  if (!root) return;

  // Find first valid media element (video or audio)
  const el = root.querySelector("video, audio") as HTMLMediaElement | null;

  if (!el) {
    throw new Error("Plyr wrapper requires a <video> or <audio> element inside the slot.");
  }

  mediaElement = el;

  let safariDurationProbeInFlight = false;
  const configuredDurationHint = Number((props.options?.duration as number | undefined) ?? 0);
  const hasConfiguredDurationHint =
    Number.isFinite(configuredDurationHint) && configuredDurationHint > 0;

  const syncDuration = () => {
    if (!player.value || !mediaElement) return;

    const duration = mediaElement.duration;

    if (hasConfiguredDurationHint) {
      // When a trusted backend duration exists, keep it unless Safari reports a close-enough
      // real value. This avoids cases where a short clip renders as hours on first load.
      if (Number.isFinite(duration) && duration > 0) {
        const durationDelta = Math.abs(duration - configuredDurationHint);
        const allowedDelta = Math.max(1, configuredDurationHint * 0.15);

        if (durationDelta <= allowedDelta) {
          player.value.duration = duration;
        }
      }
      return;
    }

    if (Number.isFinite(duration) && duration > 0) {
      player.value.duration = duration;
      return;
    }

    // Safari can temporarily report Infinity for audio duration until forced to probe the file.
    if (duration !== Infinity || safariDurationProbeInFlight) {
      return;
    }

    safariDurationProbeInFlight = true;
    const previousTime = Number.isFinite(mediaElement.currentTime) ? mediaElement.currentTime : 0;

    const handleTimeUpdate = () => {
      safariDurationProbeInFlight = false;
      mediaElement?.removeEventListener("timeupdate", handleTimeUpdate);

      if (!mediaElement) return;

      if (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0) {
        try {
          mediaElement.currentTime = previousTime;
        } catch {
          // Ignore browsers that reject restoring the probe seek.
        }
        syncDuration();
      }
    };

    mediaElement.addEventListener("timeupdate", handleTimeUpdate, { once: true });

    try {
      mediaElement.currentTime = 1e7;
    } catch {
      safariDurationProbeInFlight = false;
      mediaElement.removeEventListener("timeupdate", handleTimeUpdate);
    }
  };

  const durationEvents = ["loadedmetadata", "durationchange", "loadeddata", "canplay"];
  durationEvents.forEach((eventName) => {
    mediaElement?.addEventListener(eventName, syncDuration);
  });

  detachDurationListeners = () => {
    durationEvents.forEach((eventName) => {
      mediaElement?.removeEventListener(eventName, syncDuration);
    });
  };

  // Init Plyr
  player.value = new Plyr(el, props.options);

  // Optional: extra safety for late updates
  player.value.on("ready", () => {
    syncDuration();
  });

  syncDuration();
});

onBeforeUnmount(() => {
  detachDurationListeners?.();
  detachDurationListeners = null;
  mediaElement = null;

  if (player.value) {
    player.value.destroy();
  }
});

defineExpose({
  player,
});
</script>
